/**
 * CopyButton Atom — button that copies text to the clipboard with feedback.
 * @layer atom
 * @package @plerity/ui-core
 */

import { cn } from "../utils/cn";
import type { CopyButtonProps } from "./Clipboard.types";
import { useClipboard } from "./useClipboard";

// Default copy icon (no lucide-react dep)
function CopyIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

const sizeStyles: Record<string, string> = {
  sm: "px-2 py-1 text-xs gap-1",
  md: "px-3 py-1.5 text-sm gap-1.5",
};

export function CopyButton({ value, label, copiedLabel = "Copied!", icon, className, size = "md" }: CopyButtonProps) {
  const { copy, copied } = useClipboard();

  const displayIcon = copied ? <CheckIcon /> : (icon ?? <CopyIcon />);
  const displayLabel = copied ? copiedLabel : label;

  return (
    <>
      {/* aria-live region announces the copied state to screen readers */}
      <span aria-live="polite" className="sr-only">
        {copied ? copiedLabel : ""}
      </span>

      <button
        type="button"
        onClick={() => copy(value)}
        aria-label={copied ? copiedLabel : (label ?? "Copy to clipboard")}
        title={copied ? copiedLabel : (label ?? "Copy to clipboard")}
        className={cn(
          "inline-flex items-center justify-center rounded-md font-medium transition-colors",
          "border border-gray-300 dark:border-gray-600",
          "bg-white dark:bg-gray-800",
          copied
            ? "text-success-strong"
            : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
          sizeStyles[size],
          className
        )}
      >
        {displayIcon}
        {displayLabel && <span>{displayLabel}</span>}
      </button>
    </>
  );
}

CopyButton.displayName = "CopyButton";
